import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  analytics,
  pageAnalytics,
  builder,
  identity,
  themes,
  experiments,
  forms,
  dashboards,
  toPeriod,
  type Range,
  type Site,
  type SiteType,
  type ExperimentStatus,
  type CreateExperimentBody,
} from "./services";
import { useAnalyticsPageScope } from "./analyticsScope";

/** Property kind drives the per-kind navigation (design decision). */
export type PropertyKind = "built" | "connected" | "app";
export function kindOf(site: Site): PropertyKind {
  if (site.type === "ios" || site.type === "android") return "app";
  // web/server: a hosted-pages property (no external origins/urls) = built here;
  // a property pointed at an external site = connected (tracked only).
  const external = site.allowed_origins.length > 0 || site.urls.length > 0;
  if (site.name.toLowerCase().includes("hosted")) return "built";
  return external ? "connected" : "built";
}
export const KIND_LABEL: Record<PropertyKind, string> = {
  built: "Built site",
  connected: "Connected site",
  app: "Mobile app",
};

const HUES = ["#B84422", "#C8923D", "#3E7C74", "#5B6470", "#8A857C"];
export function hueFor(i: number): string {
  return HUES[i % HUES.length]!;
}
export function deviceHint(t: SiteType): string {
  return t === "ios" ? "iOS" : t === "android" ? "Android" : t === "server" ? "Server" : "Web";
}

/* --------------------------- workspace resources -------------------------- */
export function useSites() {
  return useQuery({ queryKey: ["sites"], queryFn: analytics.listSites });
}

/** Create a web property (returns the one-time write key); refresh the list. */
export function useCreateSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; type: SiteType; allowed_origins?: string[]; urls?: string[] }) =>
      analytics.createSite(body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["sites"] }),
  });
}
/** Rotate a property's write key (returns the new key once); refresh the list. */
export function useRotateSiteKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (siteId: string) => analytics.rotateSiteKey(siteId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["sites"] }),
  });
}
/** Revoke a property; refresh the list so it shows as revoked. */
export function useRevokeSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (siteId: string) => analytics.revokeSite(siteId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["sites"] }),
  });
}
export function useProjects() {
  return useQuery({ queryKey: ["projects"], queryFn: builder.listProjects });
}
export function usePages(projectId: string | undefined) {
  return useQuery({
    queryKey: ["pages", projectId],
    queryFn: () => builder.listPages(projectId!),
    enabled: !!projectId,
  });
}
export function useTemplates(projectId: string | undefined, category?: string) {
  return useQuery({
    queryKey: ["templates", projectId, category ?? "All"],
    queryFn: () => builder.listTemplates(projectId!, category ? { category } : undefined),
    enabled: !!projectId,
  });
}

/** A single page WITH its current_version (tree) — backs the in-app preview. */
export function usePage(pageId: string | undefined) {
  return useQuery({
    queryKey: ["page", pageId],
    queryFn: () => builder.getPage(pageId!),
    enabled: !!pageId,
  });
}

/** Project-scoped custom domains — used for assigning a domain to a page. */
export function useDomains(projectId: string | undefined) {
  return useQuery({
    queryKey: ["domains", projectId],
    queryFn: () => builder.listDomains(projectId!),
    enabled: !!projectId,
    retry: false,
  });
}

/** Create a web page, then refresh the pages list. */
export function useCreatePage(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { title: string; slug?: string; domain: string; locale?: string }) =>
      builder.createPage(projectId!, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["pages", projectId] });
    },
  });
}

/** Patch a page (title / domain / …) and refresh both the list and the page. */
export function useUpdatePage(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      pageId,
      body,
    }: {
      pageId: string;
      body: Parameters<typeof builder.updatePage>[1];
    }) => builder.updatePage(pageId, body),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ["pages", projectId] });
      void qc.invalidateQueries({ queryKey: ["page", vars.pageId] });
    },
  });
}

/** Delete a page; refresh the project's pages list. */
export function useDeletePage(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pageId: string) => builder.deletePage(pageId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["pages", projectId] });
    },
  });
}

/** Duplicate a page; refresh the pages list. Returns the new page. */
export function useDuplicatePage(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { pageId: string; title?: string }) =>
      builder.duplicatePage(vars.pageId, vars.title ? { title: vars.title } : undefined),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["pages", projectId] });
    },
  });
}

/* ------------------------------ designs (themes) -------------------------- */
export function useThemes(projectId: string | undefined) {
  return useQuery({
    queryKey: ["themes", projectId],
    queryFn: () => themes.list(projectId!),
    enabled: !!projectId,
  });
}
export function useActiveTheme(projectId: string | undefined) {
  return useQuery({
    queryKey: ["active-theme", projectId],
    queryFn: () => themes.active(projectId!),
    enabled: !!projectId,
    retry: false,
  });
}
/** Delete a saved design; refresh the gallery + active-theme. The server
 *  blocks deleting the active theme (409) — the caller surfaces that. */
export function useDeleteTheme(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (themeId: string) => themes.delete(themeId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["themes", projectId] });
      void qc.invalidateQueries({ queryKey: ["active-theme", projectId] });
    },
  });
}

export function useEntitlements() {
  return useQuery({ queryKey: ["entitlements"], queryFn: identity.entitlements, retry: false });
}

/** Recent activity (audit log) for the org; optional entity filter. */
export function useAuditLog(params?: { entity?: string; limit?: number }) {
  return useQuery({
    queryKey: ["audit-log", params?.entity ?? null, params?.limit ?? 50],
    queryFn: () => builder.listAuditLog(params),
    retry: false,
  });
}
export function useMembers(projectId: string | undefined) {
  return useQuery({
    queryKey: ["members", projectId],
    queryFn: () => builder.listMembers(projectId!),
    enabled: !!projectId,
    retry: false,
  });
}
export function useInviteMember(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; role?: string }) =>
      builder.inviteMember(projectId!, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["members", projectId] });
    },
  });
}

/* ------------------------------ analytics panels -------------------------- */
/**
 * Resolve the active analytics scope. When a page filter is set (Signals
 * "filter by page"), every panel reads the page-scoped endpoints; otherwise the
 * whole-property ones. Returns the id to query, the scope tag (for cache keys),
 * and the matching typed client — both clients share identical method shapes.
 */
function useScoped(siteId: string | undefined) {
  const pageId = useAnalyticsPageScope();
  return pageId
    ? { id: pageId, scope: "page" as const, client: pageAnalytics }
    : { id: siteId, scope: "site" as const, client: analytics };
}

export function useSummary(siteId: string | undefined, range: Range) {
  const { id, scope, client } = useScoped(siteId);
  return useQuery({
    queryKey: ["analytics", scope, id, "summary", range],
    queryFn: () => client.summary(id!, toPeriod(range)),
    enabled: !!id,
  });
}
export function useTimeseries(siteId: string | undefined, range: Range, metric = "visits") {
  const { id, scope, client } = useScoped(siteId);
  return useQuery({
    queryKey: ["analytics", scope, id, "timeseries", metric, range],
    queryFn: () => client.timeseries(id!, metric, toPeriod(range)),
    enabled: !!id,
  });
}
export function useRealtime(siteId: string | undefined) {
  const { id, scope, client } = useScoped(siteId);
  return useQuery({
    queryKey: ["analytics", scope, id, "realtime"],
    queryFn: () => client.realtime(id!),
    enabled: !!id,
    refetchInterval: 5000,
  });
}
export function useSources(siteId: string | undefined, range: Range) {
  const { id, scope, client } = useScoped(siteId);
  return useQuery({
    queryKey: ["analytics", scope, id, "sources", range],
    queryFn: () => client.sources(id!, toPeriod(range)),
    enabled: !!id,
  });
}
export function useDevices(siteId: string | undefined, range: Range) {
  const { id, scope, client } = useScoped(siteId);
  return useQuery({
    queryKey: ["analytics", scope, id, "devices", range],
    queryFn: () => client.devices(id!, toPeriod(range)),
    enabled: !!id,
  });
}
export function useGeo(siteId: string | undefined, range: Range) {
  const { id, scope, client } = useScoped(siteId);
  return useQuery({
    queryKey: ["analytics", scope, id, "geo", range],
    queryFn: () => client.geo(id!, toPeriod(range)),
    enabled: !!id,
  });
}
export function useEvents(siteId: string | undefined, range: Range) {
  const { id, scope, client } = useScoped(siteId);
  return useQuery({
    queryKey: ["analytics", scope, id, "events", range],
    queryFn: () => client.events(id!, toPeriod(range)),
    enabled: !!id,
  });
}
export function useFunnels(siteId: string | undefined, range: Range, steps?: string) {
  const { id, scope, client } = useScoped(siteId);
  return useQuery({
    queryKey: ["analytics", scope, id, "funnels", range, steps ?? ""],
    queryFn: () => client.funnels(id!, toPeriod(range), steps),
    enabled: !!id,
    // Keep the previous funnel on screen while a step edit refetches (no flash).
    placeholderData: keepPreviousData,
  });
}
export function useHeatmap(siteId: string | undefined, range: Range) {
  const { id, scope, client } = useScoped(siteId);
  return useQuery({
    queryKey: ["analytics", scope, id, "heatmap", range],
    queryFn: () => client.heatmap(id!, toPeriod(range)),
    enabled: !!id,
  });
}
export function useScroll(siteId: string | undefined, range: Range) {
  const { id, scope, client } = useScoped(siteId);
  return useQuery({
    queryKey: ["analytics", scope, id, "scroll", range],
    queryFn: () => client.scroll(id!, toPeriod(range)),
    enabled: !!id,
  });
}
export function useReplays(siteId: string | undefined) {
  const { id, scope, client } = useScoped(siteId);
  return useQuery({
    queryKey: ["analytics", scope, id, "replays"],
    queryFn: () => client.replays(id!),
    enabled: !!id,
  });
}

/** Submissions for one form — backs the in-Make submissions viewer (#19). */
export function useFormSubmissions(formId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["forms", formId, "submissions"],
    queryFn: () => forms.listSubmissions(formId!),
    enabled: !!formId && enabled,
  });
}

/** One session's full rrweb event stream — backs real replay playback (#16). */
export function useReplayDetail(siteId: string | undefined, sessionId: string | undefined, enabled = true) {
  const { id, scope, client } = useScoped(siteId);
  return useQuery({
    queryKey: ["analytics", scope, id, "replay", sessionId],
    queryFn: () => client.replay(id!, sessionId!),
    enabled: !!id && !!sessionId && enabled,
  });
}

/* ------------------------------ today: insights + digest ------------------ */
// Whole-property only (Today is never page-filtered). Both carry a `seeded`
// flag the screen uses to decide live-vs-sample provenance.
export function useInsights(siteId: string | undefined, range: Range) {
  return useQuery({
    queryKey: ["insights", siteId, range],
    queryFn: () => analytics.insights(siteId!, toPeriod(range)),
    enabled: !!siteId,
  });
}
export function useDigest(siteId: string | undefined, range: Range) {
  return useQuery({
    queryKey: ["digest", siteId, range],
    queryFn: () => analytics.digest(siteId!, toPeriod(range)),
    enabled: !!siteId,
  });
}

/* ------------------------------ experiments (A/B) ------------------------- */
/** Experiments on a single page (the Make Test tab works one page at a time). */
export function useExperiments(pageId: string | undefined) {
  return useQuery({
    queryKey: ["experiments", "page", pageId],
    queryFn: () => experiments.listByPage(pageId!),
    enabled: !!pageId,
    retry: false,
  });
}
/** All experiments across a project's pages (the Signals A/B panel scope —
 *  there is no site-level list endpoint, so we fan out over the pages). */
export function useProjectExperiments(projectId: string | undefined) {
  return useQuery({
    queryKey: ["experiments", "project", projectId],
    queryFn: async () => {
      const pages = await builder.listPages(projectId!);
      const lists = await Promise.all(
        pages.map((p) => experiments.listByPage(p.id).catch(() => ({ items: [] }))),
      );
      return lists.flatMap((l) => l.items);
    },
    enabled: !!projectId,
    retry: false,
  });
}
/** Bayesian stats for one experiment (exposures/conversions/prob_beat_control). */
export function useExperimentStats(experimentId: string | undefined) {
  return useQuery({
    queryKey: ["experiment-stats", experimentId],
    queryFn: () => experiments.stats(experimentId!),
    enabled: !!experimentId,
    retry: false,
  });
}
export function useCreateExperiment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateExperimentBody) => experiments.create(body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["experiments"] }),
  });
}
export function useSetExperimentStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; status: ExperimentStatus; winnerVariantId?: string }) =>
      experiments.setStatus(v.id, v.status, v.winnerVariantId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["experiments"] });
      void qc.invalidateQueries({ queryKey: ["experiment-stats"] });
    },
  });
}

/* ------------------------------ dashboards (#20) -------------------------- */
// Per-user custom dashboards (analytics-svc /dashboards, JWT-scoped to the
// caller within their org). Backs the Signals "My dashboard" widget picker.
export function useDashboards() {
  return useQuery({ queryKey: ["dashboards"], queryFn: dashboards.list, retry: false });
}
/** Create a dashboard, then refresh the list. */
export function useCreateDashboard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; config?: Record<string, unknown> }) =>
      dashboards.create(body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["dashboards"] }),
  });
}
/** Patch a dashboard (name / config); refresh the list. */
export function useUpdateDashboard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: Partial<{ name: string; config: Record<string, unknown> }>;
    }) => dashboards.update(id, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["dashboards"] }),
  });
}
/** Delete a dashboard; refresh the list. */
export function useDeleteDashboard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => dashboards.remove(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["dashboards"] }),
  });
}
